// Tlačítko „Historie" v patičce (úplně vlevo). Dropdown (otevíraný NAHORU) s posledními otevřenými
// agendami — používá stejný design jako hlavní menu (MenuItemList: ikony + řádky + čudlík detach).
// Klik na řádek = nahradit plochu, čudlík = samostatné okno.

import { useMemo, useState } from 'react';

import { useAgendaHistoryStore, type AgendaHistoryItem } from '@/shared/lib/agendaHistory';
import { DropdownMenu } from '@/shared/ui/dropdown-menu';
import { Icon } from '@/shared/ui/icon';
import { MenuItemList, type MenuItemNode } from '@/shared/ui';

export interface AgendaHistoryButtonProps {
  /** Klik na řádek → otevřít agendu a nahradit plochu. */
  readonly onOpen: (item: AgendaHistoryItem) => void;
  /** Čudlík → otevřít agendu v samostatném (plovoucím) okně. */
  readonly onDetach: (item: AgendaHistoryItem) => void;
}

export function AgendaHistoryButton({ onOpen, onDetach }: AgendaHistoryButtonProps) {
  const items = useAgendaHistoryStore((s) => s.items);
  const [open, setOpen] = useState(false);

  // Historie → uzly menu (key = GlobalName/path pro resolver ikon, origin = typový chip P/M).
  const nodes = useMemo<MenuItemNode[]>(
    () =>
      items.map((item) => ({
        t: 'item',
        id: item.id,
        title: item.title,
        key: item.target,
        origin: item.kind,
      })),
    [items]
  );
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  const trigger = (
    <button
      type="button"
      className="rounded-phx-sm text-phx-text-2 hover:bg-phx-surface-2 h-phx-8 w-phx-control-lg flex cursor-pointer items-center justify-center border-0 bg-transparent"
      title="Naposledy otevřené agendy"
      aria-label="Historie agend"
    >
      <Icon name="clock" size={16} />
    </button>
  );

  return (
    <DropdownMenu
      trigger={trigger}
      align="start"
      placement="top"
      backdrop
      open={open}
      onOpenChange={setOpen}
      className="p-phx-1 max-h-[min(70vh,520px)] w-[340px] overflow-y-auto"
    >
      <MenuItemList
        items={nodes}
        onOpen={(node) => {
          const item = byId.get(node.id);
          if (item) onOpen(item);
          setOpen(false);
        }}
        onDetach={(node) => {
          const item = byId.get(node.id);
          if (item) onDetach(item);
          setOpen(false);
        }}
        empty={
          <div className="px-phx-3 py-phx-2 text-phx-text-muted text-phx-sm">Žádná historie</div>
        }
      />
    </DropdownMenu>
  );
}
